/**
 * Playlist policy for mood music (ISS-0183).
 *
 * Kept out of the AudioEngine on purpose: the engine plays buffers and says
 * when one is about to end, and this decides what comes next. Pure, so the
 * shuffle can be tested with a seeded RNG.
 */

export interface ShuffleDraw<T> {
  track: T;
  /** Track ids still to play this cycle. Pass it back in on the next draw. */
  bag: string[];
}

/**
 * Draw the next track from a shuffle bag.
 *
 * Every track plays once per cycle in shuffled order, and the first track of a
 * new cycle is never the one that just finished, so a mood never plays the
 * same track twice in a row. A plain random pick would allow both repeats and
 * long droughts for one track.
 *
 * `bag` may be stale (tracks removed from the playlist since it was drawn);
 * ids that are no longer in the playlist are dropped.
 */
export function drawFromShuffleBag<T extends { id: string }>(
  playlist: readonly T[],
  bag: readonly string[],
  lastId: string | null,
  rng: () => number = Math.random,
): ShuffleDraw<T> | null {
  if (playlist.length === 0) return null;
  if (playlist.length === 1) return { track: playlist[0], bag: [] };

  const byId = new Map(playlist.map((t) => [t.id, t]));
  let remaining = bag.filter((id) => byId.has(id));

  // The head of a stale bag can be the track that just played (the GM picked
  // it by hand); skip past it rather than repeat it.
  let index = remaining.findIndex((id) => id !== lastId);
  if (index === -1) {
    remaining = shuffle([...byId.keys()], rng);
    if (remaining[0] === lastId) {
      const swapWith = 1 + Math.floor(rng() * (remaining.length - 1));
      [remaining[0], remaining[swapWith]] = [remaining[swapWith], remaining[0]];
    }
    index = 0;
  }

  const id = remaining[index];
  return {
    track: byId.get(id)!,
    bag: [...remaining.slice(0, index), ...remaining.slice(index + 1)],
  };
}

/** Fisher-Yates, in place. */
function shuffle<T>(items: T[], rng: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}
